import { useCallback, useEffect, useState, type FormEvent } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import { CheckCircle2, Eye, ListFilter, Mail, PackageCheck, Search, XCircle } from 'lucide-react'
import ConfirmDialog from './ConfirmDialog'
import Modal from './Modal'
import {
  formatPrice,
  orderStatusLabels,
  paymentMethodLabels,
  paymentStatusLabels,
  type OrderStatus,
  type PaymentMethod,
  type ShopOrder,
} from '../types/Shop'

const nextStatus: Partial<Record<OrderStatus, OrderStatus>> = {
  PENDING: 'CONFIRMED',
  CONFIRMED: 'READY_FOR_PICKUP',
  READY_FOR_PICKUP: 'COMPLETED',
}

const nextStatusLabels: Partial<Record<OrderStatus, string>> = {
  PENDING: 'Confirmer',
  CONFIRMED: 'Marquer prête',
  READY_FOR_PICKUP: 'Marquer remise',
}

const activePaymentMethods: PaymentMethod[] = ['CASH', 'CHECK', 'BANK_TRANSFER']

const ShopOrdersAdminModule = () => {
  const [orders, setOrders] = useState<ShopOrder[]>([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState<ShopOrder | null>(null)
  const [toCancel, setToCancel] = useState<ShopOrder | null>(null)
  const [busy, setBusy] = useState(false)
  const [filters, setFilters] = useState({ search: '', status: '', paymentStatus: '' })

  const loadOrders = useCallback(async (activeFilters: typeof filters) => {
    setLoading(true)
    try {
      const { data } = await axios.get<ShopOrder[]>('/api/shop/admin/orders', {
        params: Object.fromEntries(Object.entries(activeFilters).filter(([, value]) => value)),
      })
      setOrders(data)
      setSelected((current) => current ? data.find((order) => order.id === current.id) ?? null : null)
    } catch {
      toast.error('Impossible de charger les commandes')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void loadOrders({ search: '', status: '', paymentStatus: '' }) }, [loadOrders])

  const applyFilters = (event: FormEvent) => {
    event.preventDefault()
    void loadOrders(filters)
  }

  const updateStatus = async (order: ShopOrder, status: OrderStatus, refundPaid = false) => {
    setBusy(true)
    try {
      await axios.patch(`/api/shop/admin/orders/${order.id}/status`, { status, refundPaid })
      toast.success('Statut mis à jour')
      await loadOrders(filters)
    } catch (error) {
      toast.error(axios.isAxiosError(error) ? error.response?.data?.message || 'Impossible de modifier le statut' : 'Impossible de modifier le statut')
    } finally {
      setBusy(false)
    }
  }

  const updatePayment = async (order: ShopOrder, status: 'PENDING' | 'PAID', method?: PaymentMethod) => {
    setBusy(true)
    try {
      await axios.patch(`/api/shop/admin/orders/${order.id}/payment`, { status, method })
      toast.success(status === 'PAID' ? 'Paiement validé' : 'Moyen de paiement modifié')
      await loadOrders(filters)
    } catch (error) {
      toast.error(axios.isAxiosError(error) ? error.response?.data?.message || 'Impossible de modifier le paiement' : 'Impossible de modifier le paiement')
    } finally {
      setBusy(false)
    }
  }

  const resendAccess = async (order: ShopOrder) => {
    setBusy(true)
    try {
      await axios.post(`/api/shop/admin/orders/${order.id}/resend-access`)
      toast.success('Nouveau lien envoyé')
    } catch (error) {
      toast.error(axios.isAxiosError(error) ? error.response?.data?.message || 'Impossible d’envoyer le lien' : 'Impossible d’envoyer le lien')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-6">
      <form className="form-section shop-order-filters" onSubmit={applyFilters}>
        <div className="shop-order-filter-heading">
          <span aria-hidden="true"><ListFilter /></span>
          <div><p className="eyebrow !mb-1">Vue opérationnelle</p><h3>Filtrer les commandes</h3><small>Retrouvez rapidement une commande ou une étape de traitement.</small></div>
        </div>
        <label className="form-field" htmlFor="order-search"><span className="form-label">Recherche</span><div className="shop-search-field"><Search aria-hidden="true" /><input id="order-search" className="input-field" placeholder="Numéro, nom ou e-mail" value={filters.search} onChange={(event) => setFilters({ ...filters, search: event.target.value })} /></div></label>
        <label className="form-field" htmlFor="order-status"><span className="form-label">Commande</span><select id="order-status" className="input-field" value={filters.status} onChange={(event) => setFilters({ ...filters, status: event.target.value })}><option value="">Tous les états</option>{Object.entries(orderStatusLabels).map(([value, label]) => <option value={value} key={value}>{label}</option>)}</select></label>
        <label className="form-field" htmlFor="order-payment-status"><span className="form-label">Paiement</span><select id="order-payment-status" className="input-field" value={filters.paymentStatus} onChange={(event) => setFilters({ ...filters, paymentStatus: event.target.value })}><option value="">Tous les états</option><option value="PENDING">À payer</option><option value="PAID">Payée</option><option value="REFUNDED">Remboursée</option></select></label>
        <button type="submit" className="btn-primary"><Search className="h-4 w-4" /> Filtrer</button>
      </form>

      {loading ? <div className="shop-orders-loading">Chargement des commandes…</div> : orders.length === 0 ? (
        <div className="empty-state"><PackageCheck className="mx-auto mb-4 h-10 w-10 text-emerald-600" /><h3>Aucune commande</h3><p className="mt-2 text-gray-600">Aucune commande ne correspond aux filtres.</p></div>
      ) : (
        <div className="data-table-wrap">
          <table className="admin-table">
            <thead><tr><th>Commande</th><th>Client</th><th>État</th><th>Paiement</th><th>Total</th><th>Action</th></tr></thead>
            <tbody>
              {orders.map((order) => (
                <tr key={order.id}>
                  <td><strong>{order.orderNumber}</strong><br /><small>{new Date(order.createdAt).toLocaleDateString('fr-FR')}</small></td>
                  <td><strong>{order.firstName} {order.lastName}</strong><br /><small>{order.email}</small></td>
                  <td><span className={`shop-status shop-status-${order.status.toLowerCase()}`}>{orderStatusLabels[order.status]}</span></td>
                  <td>{order.payment && <><strong>{paymentStatusLabels[order.payment.status]}</strong><br /><small>{paymentMethodLabels[order.payment.method]}</small></>}</td>
                  <td><strong>{formatPrice(order.totalCents)}</strong></td>
                  <td><button type="button" className="icon-button" onClick={() => setSelected(order)} aria-label={`Voir ${order.orderNumber}`}><Eye /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal isOpen={selected !== null} onClose={() => !busy && setSelected(null)} title={selected?.orderNumber ?? 'Commande'} eyebrow="Gestion Boutique" size="lg">
        {selected && (
          <div className="space-y-6">
            <div className="shop-admin-order-head">
              <div><small>Client</small><strong>{selected.firstName} {selected.lastName}</strong><span>{selected.email}</span></div>
              <div><small>Total</small><strong>{formatPrice(selected.totalCents)}</strong><span>{new Date(selected.createdAt).toLocaleString('fr-FR')}</span></div>
            </div>
            <div className="order-lines">
              {selected.items.map((item) => <div className="order-line" key={item.id}><img src={item.imageUrl} alt="" /><div><strong>{item.productName}</strong><small>{item.variantLabel} · Quantité {item.quantity}</small>{item.memberQuantity > 0 && <small className="member-price-note">Tarif adhérent appliqué</small>}</div><strong>{formatPrice(item.lineTotalCents)}</strong></div>)}
            </div>
            <div className="shop-admin-actions">
              <div>
                <p className="eyebrow !mb-2">Préparation</p>
                <span className={`shop-status shop-status-${selected.status.toLowerCase()}`}>{orderStatusLabels[selected.status]}</span>
                <div className="mt-3 flex flex-wrap gap-2">
                  {nextStatus[selected.status] && <button type="button" className="btn-primary" disabled={busy || (nextStatus[selected.status] === 'COMPLETED' && selected.payment?.status !== 'PAID')} title={nextStatus[selected.status] === 'COMPLETED' && selected.payment?.status !== 'PAID' ? 'Validez d’abord le paiement' : undefined} onClick={() => void updateStatus(selected, nextStatus[selected.status]!)}><CheckCircle2 className="h-4 w-4" /> {nextStatusLabels[selected.status]}</button>}
                  {!['COMPLETED', 'CANCELLED'].includes(selected.status) && <button type="button" className="btn-danger" disabled={busy} onClick={() => setToCancel(selected)}><XCircle className="h-4 w-4" /> Annuler</button>}
                </div>
              </div>
              <div>
                <p className="eyebrow !mb-2">Paiement</p>
                <strong>{selected.payment ? paymentStatusLabels[selected.payment.status] : 'Inconnu'}</strong>
                {selected.payment?.status === 'PENDING' && !['COMPLETED', 'CANCELLED'].includes(selected.status) && (
                  <div className="shop-payment-editor">
                    <label className="form-field" htmlFor={`order-payment-method-${selected.id}`}>
                      <span className="form-label">Moyen de paiement</span>
                      <select id={`order-payment-method-${selected.id}`} className="input-field" value={selected.payment.method} disabled={busy} onChange={(event) => void updatePayment(selected, 'PENDING', event.target.value as PaymentMethod)}>{activePaymentMethods.map((method) => <option value={method} key={method}>{paymentMethodLabels[method]}</option>)}</select>
                    </label>
                    <button type="button" className="btn-secondary" disabled={busy} onClick={() => void updatePayment(selected, 'PAID')}><CheckCircle2 className="h-4 w-4" /> Marquer payée</button>
                  </div>
                )}
              </div>
            </div>
            {selected.guestBuyerId && <button type="button" className="btn-secondary" disabled={busy} onClick={() => void resendAccess(selected)}><Mail className="h-4 w-4" /> Renvoyer le lien personnel</button>}
          </div>
        )}
      </Modal>

      <ConfirmDialog
        isOpen={toCancel !== null}
        title="Annuler la commande"
        message={toCancel?.payment?.status === 'PAID' ? 'Confirmez que le remboursement a été traité. Le stock et l’avantage adhérent seront restitués.' : 'Le stock et l’avantage adhérent seront immédiatement restitués.'}
        confirmLabel={toCancel?.payment?.status === 'PAID' ? 'Remboursée et annulée' : 'Annuler la commande'}
        isBusy={busy}
        onCancel={() => setToCancel(null)}
        onConfirm={async () => {
          if (!toCancel) return
          await updateStatus(toCancel, 'CANCELLED', toCancel.payment?.status === 'PAID')
          setToCancel(null)
        }}
      />
    </div>
  )
}

export default ShopOrdersAdminModule
